import React, { useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Dialog, Slide, IconButton, Tooltip, useMediaQuery } from "@material-ui/core";
import CallEndIcon from "@material-ui/icons/CallEnd";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import PersonIcon from "@material-ui/icons/Person";
import { useVoice } from "../../context/Voice/VoiceContext";
import SoftphoneControls from "../SoftphoneControls";
import toastError from "../../errors/toastError";

/**
 * SoftphoneCallModal — the in-call experience.
 *   • phones + small tablets (< md): a FULL-SCREEN, Apple-style call screen.
 *   • desktop / large tablet (>= md): a translucent frosted brand-green window.
 * Opens while a call is live, closes on end (the Outcome panel takes over).
 * Stable layout: the hang-up button is centred in its own bottom row and never
 * shifts when the mute/speaker controls appear on connect.
 */
const BRAND = "#25d366";
const ENDED = ["completed", "no_answer", "busy", "failed"];

const STATE_OF = (s) => {
  if (["queued", "created", "initiated"].includes(s))
    return { label: "מחייג…", color: "#1e88e5", pulse: true };
  if (s === "ringing") return { label: "מצלצל…", color: "#f9a825", pulse: true };
  if (s === "active") return { label: "מחובר", color: "#2e7d32", pulse: false };
  if (s === "no_answer") return { label: "לא נענתה", color: "#c62828", pulse: false };
  if (s === "busy") return { label: "תפוס", color: "#c62828", pulse: false };
  if (s === "failed") return { label: "נכשלה", color: "#c62828", pulse: false };
  return { label: "הסתיימה", color: "#757575", pulse: false };
};

const mmss = (n) =>
  `${String(Math.floor((n || 0) / 60)).padStart(2, "0")}:${String((n || 0) % 60).padStart(2, "0")}`;

// Module-level start anchor keyed by call id. Living OUTSIDE the component makes
// the timer immune to remounts/re-renders — the start instant is locked ONCE per
// call (preferring the server answeredAt) and never re-anchored, so the counter
// is perfectly continuous (no 15→36 jumps) on phones, tablets and desktop alike.
const CALL_START = new Map();
const callStartMs = (call) => {
  if (!call || !call.id) return null;
  if (!CALL_START.has(call.id)) {
    CALL_START.set(
      call.id,
      call.answeredAt ? new Date(call.answeredAt).getTime() : Date.now()
    );
  }
  return CALL_START.get(call.id);
};

const Transition = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface = dark
    ? "linear-gradient(180deg, rgba(18,26,22,0.94), rgba(11,17,14,0.97))"
    : "linear-gradient(180deg, rgba(255,255,255,0.93), rgba(242,250,245,0.96))";
  return {
    paper: {
      background: surface,
      backdropFilter: "blur(26px) saturate(150%)",
      WebkitBackdropFilter: "blur(26px) saturate(150%)",
      border: `1px solid ${dark ? "rgba(46,227,122,0.2)" : "rgba(37,211,102,0.24)"}`,
      borderRadius: 28,
      boxShadow: "0 28px 80px rgba(0,0,0,0.4)",
      width: 380,
      maxWidth: "92vw",
      overflow: "hidden",
    },
    paperFull: {
      background: surface,
      backdropFilter: "blur(26px) saturate(150%)",
      WebkitBackdropFilter: "blur(26px) saturate(150%)",
      borderRadius: 0,
      margin: 0,
      width: "100%",
      maxWidth: "100%",
      height: "100%",
    },
    "@keyframes ringPulse": {
      "0%": { transform: "scale(0.85)", opacity: 0.6 },
      "100%": { transform: "scale(1.5)", opacity: 0 },
    },
    "@keyframes blink": { "0%,100%": { opacity: 1 }, "50%": { opacity: 0.25 } },
    wrap: {
      direction: "rtl",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "30px 26px 26px",
    },
    wrapFull: {
      minHeight: "100%",
      justifyContent: "space-between",
      padding: "64px 28px 48px",
    },
    top: { display: "flex", flexDirection: "column", alignItems: "center", width: "100%" },
    avatarWrap: { position: "relative", marginBottom: 18 },
    avatar: {
      width: 108,
      height: 108,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(46,227,122,0.12)" : "rgba(37,211,102,0.12)",
      color: BRAND,
      position: "relative",
      zIndex: 1,
      overflow: "hidden",
    },
    avatarFull: { width: 132, height: 132 },
    avatarImg: { width: "100%", height: "100%", objectFit: "cover" },
    halo: {
      position: "absolute",
      inset: 0,
      borderRadius: "50%",
      border: `2px solid ${BRAND}`,
      animation: "$ringPulse 1.5s ease-out infinite",
    },
    name: {
      fontSize: 22,
      fontWeight: 600,
      color: dark ? "#eafff2" : "#0B0D0C",
      textAlign: "center",
      maxWidth: "100%",
      wordBreak: "break-word",
    },
    nameFull: { fontSize: 27 },
    sub: { fontSize: 14, color: dark ? "rgba(230,247,236,0.6)" : "#5f6f6a", marginTop: 3, direction: "ltr" },
    statusRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 14 },
    dot: { width: 9, height: 9, borderRadius: "50%" },
    statusLabel: { fontSize: 14, fontWeight: 700 },
    timer: {
      fontSize: 32,
      fontWeight: 600,
      fontVariantNumeric: "tabular-nums",
      letterSpacing: 1,
      color: dark ? "#eafff2" : "#0B0D0C",
      marginTop: 12,
    },
    rec: { display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, marginTop: 10, color: dark ? "#ff8a80" : "#a32d2d" },
    recDot: { fontSize: 10, animation: "$blink 1.2s ease-in-out infinite" },
    // Bottom cluster: a FIXED-height control area (so the hang-up never shifts
    // when mute/speaker appear on connect) + the centred hang-up row.
    bottom: { width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginTop: 26 },
    controlsArea: { minHeight: 52, display: "flex", alignItems: "center", justifyContent: "center" },
    hangRow: { display: "flex", justifyContent: "center", marginTop: 18 },
    hangBtn: {
      width: 66,
      height: 66,
      borderRadius: "50%",
      background: "#d32f2f",
      color: "#fff",
      boxShadow: "0 8px 22px rgba(211,47,47,0.4)",
      transition: "transform .08s ease, background .15s ease",
      "&:active": { transform: "scale(0.92)" },
      "@media (hover:hover)": { "&:hover": { background: "#b71c1c" } },
    },
    hangBtnFull: { width: 74, height: 74 },
  };
});

const SoftphoneCallModal = () => {
  const classes = useStyles();
  const theme = useTheme();
  // Full-screen on phones + small tablets; windowed on desktop / large tablet.
  const isFull = useMediaQuery(theme.breakpoints.down("sm"));
  const { status, activeCall, hangup, callPeer } = useVoice();
  const [, force] = useState(0);

  const isConnected = activeCall && activeCall.status === "active";
  const isEnded = activeCall && ENDED.includes(activeCall.status);

  // Steady 1 Hz repaint while connected — the elapsed value itself is derived
  // from the locked module-level anchor, so it advances by exactly one second.
  useEffect(() => {
    if (!isConnected) return undefined;
    const t = setInterval(() => force((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [isConnected]);

  // Drop the anchor once the call is fully gone (keeps the map from growing).
  useEffect(() => {
    if (!activeCall) CALL_START.clear();
  }, [activeCall]);

  const open = Boolean(status && status.enabled && activeCall && !isEnded);
  if (!activeCall) return null;

  const st = STATE_OF(activeCall.status);
  const startMs = isConnected ? callStartMs(activeCall) : null;
  const elapsed = startMs ? Math.max(0, Math.floor((Date.now() - startMs) / 1000)) : 0;

  // Peer identity comes from callPeer (captured at dial time) — the modal is now
  // session-level and has no ticket prop. Falls back to the dialed number.
  const peerName = callPeer && callPeer.name;
  const name = peerName || activeCall.toNumber || "שיחה";
  const numberSub = peerName ? activeCall.toNumber : null;
  const avatarUrl = callPeer && callPeer.avatar;

  const onHangup = async () => {
    try {
      await hangup();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onHangup}
      fullScreen={isFull}
      TransitionComponent={Transition}
      classes={{ paper: isFull ? classes.paperFull : classes.paper }}
      BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.4)", backdropFilter: "blur(2px)" } }}
    >
      <div className={`${classes.wrap} ${isFull ? classes.wrapFull : ""}`}>
        <div className={classes.top}>
          <div className={classes.avatarWrap}>
            {st.pulse && <span className={classes.halo} style={{ borderColor: st.color }} />}
            <div className={`${classes.avatar} ${isFull ? classes.avatarFull : ""}`}>
              {avatarUrl ? (
                <img className={classes.avatarImg} src={avatarUrl} alt="" />
              ) : (
                <PersonIcon style={{ fontSize: isFull ? 64 : 54 }} />
              )}
            </div>
          </div>

          <div className={`${classes.name} ${isFull ? classes.nameFull : ""}`}>{name}</div>
          {numberSub && <div className={classes.sub}>{numberSub}</div>}

          <div className={classes.statusRow}>
            <span className={classes.dot} style={{ background: st.color }} />
            <span className={classes.statusLabel} style={{ color: st.color }}>{st.label}</span>
          </div>

          {isConnected && <div className={classes.timer}>{mmss(elapsed)}</div>}

          {status.recordingEnabled && isConnected && (
            <div className={classes.rec}>
              <FiberManualRecordIcon className={classes.recDot} />
              מקליט
            </div>
          )}
        </div>

        <div className={classes.bottom}>
          {/* Fixed-height: mute/speaker appear here on connect WITHOUT moving the
              hang-up button below. */}
          <div className={classes.controlsArea}>
            <SoftphoneControls />
          </div>
          <div className={classes.hangRow}>
            <Tooltip title="סיים שיחה">
              <IconButton
                className={`${classes.hangBtn} ${isFull ? classes.hangBtnFull : ""}`}
                onClick={onHangup}
                aria-label="סיים שיחה"
              >
                <CallEndIcon style={{ fontSize: isFull ? 34 : 30 }} />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default SoftphoneCallModal;
